import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  REVIEW_MAX_MEDIA_BYTES,
  buildReviewFormData,
  mapReviewDetail,
} from "./adminCityReviewsData"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"]

const validationSchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required("Review title is required")
    .max(150, "Title must be 150 characters or fewer"),
  description: yup
    .string()
    .trim()
    .required("Your review is required")
    .max(1000, "Review must be 1000 characters or fewer"),
  previews: yup
    .array()
    .min(1, "Please upload a cover image")
    .max(1, "Only one image is allowed"),
})

const revokePreviewUrls = (previews = []) => {
  ;(Array.isArray(previews) ? previews : []).forEach(item => {
    if (item?.url?.startsWith("blob:")) URL.revokeObjectURL(item.url)
  })
}

const createFilePreview = file => ({
  id: `local-${file.name}-${file.size}-${file.lastModified}`,
  url: URL.createObjectURL(file),
  name: file.name,
  file,
  isExisting: false,
  mediaId: null,
})

/**
 * Create / edit admin city review — Figma Add a Review modal
 * (`40004879:33614`), shared layout with City Hub review modal.
 */
const ReviewFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  cityId,
  reviewId,
}) => {
  const isEdit = Boolean(reviewId)
  const fileInputRef = useRef(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    previews: [],
    initialMediaIds: [],
    removedMediaIds: [],
  })

  useEffect(() => {
    if (!isOpen) return undefined

    let cancelled = false

    const loadDetail = async () => {
      if (!reviewId) {
        setInitialValues({
          title: "",
          description: "",
          previews: [],
          initialMediaIds: [],
          removedMediaIds: [],
        })
        return
      }

      setLoadingDetail(true)
      try {
        const response = await get(endpoints.getAdminReviewById(reviewId))
        if (cancelled) return
        if (response?.data?.success) {
          const detail = mapReviewDetail(response.data.result || {})
          const firstMedia = (detail.medias || [])[0]
          const existing = firstMedia
            ? [
                {
                  id: firstMedia.id,
                  url: firstMedia.media_url || "",
                  name: "Cover image",
                  file: null,
                  isExisting: true,
                  mediaId: firstMedia.id,
                },
              ]
            : []
          const allIds = (detail.medias || [])
            .map(media => media.id)
            .filter(Boolean)
          setInitialValues({
            title: detail.title === "--" ? "" : detail.title,
            description: detail.description || "",
            previews: existing,
            initialMediaIds: allIds,
            removedMediaIds: allIds.slice(1),
          })
        } else {
          toastApiError(response, "Failed to load review")
          onClose()
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load review")
          onClose()
        }
      } finally {
        if (!cancelled) setLoadingDetail(false)
      }
    }

    loadDetail()
    return () => {
      cancelled = true
    }
  }, [isOpen, onClose, reviewId])

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setFieldError, setFieldTouched },
  ) => {
    try {
      const formData = buildReviewFormData({
        values,
        cityId,
        isEdit,
      })
      const response = isEdit
        ? await post(endpoints.updateAdminReview(reviewId), formData, {
            skipErrorToast: true,
          })
        : await post(endpoints.createAdminReview, formData, {
            skipErrorToast: true,
          })

      if (response?.data?.success) {
        toast.success(
          response.data.message ||
            (isEdit ? "Review updated." : "Review submitted."),
          TOAST_OPTIONS,
        )
        revokePreviewUrls(values.previews)
        resetForm()
        onSuccess?.()
        onClose()
      } else {
        reportFormApiError(
          response,
          isEdit ? "Failed to update review" : "Failed to create review",
          { setFieldError, setFieldTouched },
        )
      }
    } catch (err) {
      reportFormApiError(
        err,
        isEdit ? "Failed to update review" : "Failed to create review",
        { setFieldError, setFieldTouched },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--city-review"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body city-hub-review-modal__body">
        <Formik
          key={
            isOpen
              ? `review-form-${reviewId || "new"}-${loadingDetail ? "loading" : "ready"}`
              : "review-form-closed"
          }
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            setFieldError,
            isSubmitting,
          }) => {
            const artworkPreview = values.previews[0] || null

            const acceptArtwork = fileList => {
              setFieldTouched("previews", true, false)
              const file = Array.from(fileList || []).find(item =>
                ACCEPTED_TYPES.includes(String(item.type || "").toLowerCase()),
              )
              if (!file) {
                setFieldError(
                  "previews",
                  "Please upload a PNG, JPG, or WEBP image",
                )
                return
              }
              if (file.size > REVIEW_MAX_MEDIA_BYTES) {
                setFieldError("previews", "Image must be 5MB or smaller")
                return
              }

              revokePreviewUrls(values.previews)
              const nextRemoved = [...(values.removedMediaIds || [])]
              values.previews.forEach(item => {
                if (item?.isExisting && item?.mediaId) {
                  nextRemoved.push(item.mediaId)
                }
              })
              setFieldValue("removedMediaIds", [...new Set(nextRemoved)])
              setFieldValue("previews", [createFilePreview(file)])
              setFieldError("previews", undefined)
            }

            const clearArtwork = () => {
              const nextRemoved = [...(values.removedMediaIds || [])]
              values.previews.forEach(item => {
                if (item?.isExisting && item?.mediaId) {
                  nextRemoved.push(item.mediaId)
                }
              })
              revokePreviewUrls(values.previews)
              setFieldValue("removedMediaIds", [...new Set(nextRemoved)])
              setFieldValue("previews", [])
              setFieldTouched("previews", true, false)
            }

            return (
              <Form className="city-hub-review-modal">
                <div className="city-hub-review-modal__main">
                  <div className="city-hub-review-modal__header">
                    <h3 className="city-hub-review-modal__title">
                      {isEdit ? "Edit Review" : "Add a Review"}
                    </h3>
                    <p className="city-hub-review-modal__help">
                      {isEdit
                        ? "Update the review details and cover image."
                        : "Your review will be sent to the admin for approval before being published."}
                    </p>
                  </div>

                  {loadingDetail ? (
                    <div
                      className="admin-city-reviews__detail-skeleton"
                      aria-busy="true"
                    >
                      <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--line" />
                      <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--line" />
                      <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--media" />
                    </div>
                  ) : (
                    <div className="city-hub-review-modal__fields">
                      <CustomInput
                        label="Review Title"
                        name="title"
                        placeholder="e.g.: Great collaboration experience"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("title", true)}
                        error={touched.title && errors.title}
                      />

                      <CustomInput
                        label="Your Review"
                        name="description"
                        textarea
                        rows={4}
                        placeholder="Write your full review - share your experience, what stood out, and any recommendations..."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("description", true)}
                        error={touched.description && errors.description}
                      />

                      <input
                        ref={fileInputRef}
                        type="file"
                        id="admin-review-artwork-upload"
                        className="city-hub-review-modal__upload-input"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={e => {
                          acceptArtwork(e.target.files)
                          e.target.value = ""
                        }}
                      />

                      {artworkPreview?.url ? (
                        <div className="city-hub-review-modal__artwork-preview">
                          <img
                            src={artworkPreview.url}
                            alt={artworkPreview.name || "Cover image"}
                          />
                          <div className="city-hub-review-modal__artwork-actions">
                            <CustomButton
                              variant="secondary"
                              size="sm"
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Replace
                            </CustomButton>
                            <CustomButton
                              variant="danger"
                              size="sm"
                              type="button"
                              onClick={clearArtwork}
                            >
                              Remove
                            </CustomButton>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="city-hub-review-modal__upload"
                          role="button"
                          tabIndex={0}
                          onClick={() => fileInputRef.current?.click()}
                          onKeyDown={event => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              fileInputRef.current?.click()
                            }
                          }}
                          onDragOver={event => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                          onDrop={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            acceptArtwork(event.dataTransfer?.files)
                          }}
                        >
                          <span
                            className="city-hub-review-modal__upload-icon"
                            aria-hidden="true"
                          >
                            <img
                              src={ICONS.callUploadExport}
                              alt=""
                              width={16}
                              height={16}
                            />
                          </span>
                          <p className="city-hub-review-modal__upload-title">
                            Upload album artwork or cover image
                          </p>
                          <p className="city-hub-review-modal__upload-hint">
                            PNG, JPG (single image, max 5MB)
                          </p>
                        </div>
                      )}
                      {touched.previews && errors.previews ? (
                        <p className="amok-field__message amok-field__message--error">
                          {errors.previews}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="city-hub-review-modal__actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="city-hub-review-modal__cancel"
                    onClick={onClose}
                    disabled={isSubmitting || loadingDetail}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="city-hub-review-modal__submit"
                    loading={isSubmitting}
                    disabled={isSubmitting || loadingDetail}
                  >
                    {isEdit ? "Update Review" : "Submit Review"}
                  </CustomButton>
                </div>
              </Form>
            )
          }}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

ReviewFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  cityId: PropTypes.string,
  reviewId: PropTypes.string,
}

ReviewFormModal.defaultProps = {
  onSuccess: undefined,
  cityId: undefined,
  reviewId: undefined,
}

export default ReviewFormModal
